import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import Button from '../Button';
import getRedirectUrlAfterLogin from 'utils/getRedirectUrlAfterLogin';

const useStyles = makeStyles((theme) => ({
  section: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),
    backgroundColor: '#FFFFFF',
  },
  container: {
    maxWidth: '1280px',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(6),
    animation: '$fadeIn 0.6s ease-out',
  },
  title: {
    fontSize: '2.25rem',
    fontWeight: 700,
    color: '#121212',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      fontSize: '2.25rem',
    },
  },
  featuresContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(6),
    maxWidth: '1120px',
    margin: '0 auto',
  },
  featureItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '2px solid #F5F5F5',
    width: '100%',
    transition: 'all 0.3s ease',
    animation: '$fadeInUp 0.8s ease-out',
    '&:hover': {
      borderColor: '#D91A5B',
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    },
    [theme.breakpoints.up('md')]: {
      width: 'calc(33.333% - 16px)',
    },
  },
  iconCircle: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    border: '2px solid #D91A5B',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    fontSize: '32px',
    color: '#D91A5B',
  },
  iconImage: {
    width: '32px',
    height: '32px',
    filter:
      'invert(23%) sepia(89%) saturate(3026%) hue-rotate(320deg) brightness(91%) contrast(93%)',
  },
  featureText: {
    textAlign: 'center',
    color: '#121212',
    lineHeight: 1.7,
    fontSize: '0.875rem',
  },
  ctaContainer: {
    textAlign: 'center',
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const features = [
  {
    icon: 'illustrations',
    text: '75,000+ illustrations across 80+ research fields',
  },
  {
    icon: 'templates',
    text: '300+ templates for all diagram types',
  },
  {
    icon: EditIcon,
    text: 'Fully editable SVGs',
  },
  {
    icon: DownloadIcon,
    text: 'Watermark-free export formats (PNG, PDF, SVG, TIFF)',
  },
  {
    icon: 'ai',
    text: 'AI-generated scientific visuals',
  },
];

export default function FeatureRecap() {
  const classes = useStyles();

  const handleCreateNowClick = () => {
    window.location.href = getRedirectUrlAfterLogin();
  };

  const renderIcon = (icon) => {
    if (icon === 'illustrations') {
      return (
        <img
          src="/images/scientific-diagram-maker/illustrations-icon.png"
          alt=""
          className={classes.iconImage}
        />
      );
    } else if (icon === 'templates') {
      return (
        <img
          src="/images/scientific-diagram-maker/templates-icon.png"
          alt=""
          className={classes.iconImage}
        />
      );
    } else if (icon === 'ai') {
      return (
        <img
          src="/images/scientific-diagram-maker/ai-icon.png"
          alt=""
          className={classes.iconImage}
        />
      );
    } else {
      const IconComponent = icon;
      return <IconComponent className={classes.icon} />;
    }
  };

  return (
    <section className={classes.section}>
      <Container className={classes.container}>
        <Box className={classes.header}>
          <Typography className={classes.title} component="h3">
            Level Up with the Best Scientific Drawing Software
          </Typography>
        </Box>

        <Box className={classes.featuresContainer}>
          {features.map((feature, index) => (
            <Box
              key={index}
              className={classes.featureItem}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <Box className={classes.iconCircle}>{renderIcon(feature.icon)}</Box>
              <Typography className={classes.featureText}>{feature.text}</Typography>
            </Box>
          ))}
        </Box>

        <Box className={classes.ctaContainer}>
          <Button size="large" variant="contained" onClick={handleCreateNowClick} component="a">
            Create Now – It's Free
          </Button>
        </Box>
      </Container>
    </section>
  );
}
